import { grey } from '@ant-design/colors'
import { faInbox, faRotate, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ConfigProvider, Empty, Flex, Input, Row, theme, Typography } from 'antd'
import { useContext, useState } from 'react'
import PageSidebar from '../common/PageSidebar'
import SidebarCreateButton from '../common/SidebarCreateButton'
import VSpace from '../common/VSpace'
import { filterUsers } from './usersModel'
import UsersPageContext from './UsersPageContext'
import { useUsersQuery, useUsersQueryInvalidator } from './usersQueries'
import UsersSidebarList from './UsersSidebarList'

const { Text } = Typography

type Props = {
  onUserSelect: (userEmail: string) => void
}

const UsersSidebar = ({ onUserSelect }: Props) => {
  const { isDeletedUser } = useContext(UsersPageContext)

  const usersQuery = useUsersQuery(isDeletedUser)
  const invalidator = useUsersQueryInvalidator()
  const [search, setSearch] = useState('')

  const activeUsers = usersQuery.data?.filter(user => user.isActive) ?? []
  const inactiveUsers = usersQuery.data?.filter(user => !user.isActive) ?? []

  const filteredUsers = filterUsers(usersQuery.data ?? [], search)

  const isSearchActive = search.trim() !== ''

  return (
    <PageSidebar>
      <div>
        <VSpace size='large'>
          <VSpace style={{ padding: '10px 10px 0 10px' }}>
            <Row justify='space-between' align='middle'>
              <Text style={{ color: 'white', fontSize: 18, fontFamily: 'Open Sans, sans-serif' }}>
                {isDeletedUser && 'DELETED'} Users
              </Text>
              <FontAwesomeIcon icon={faRotate} style={{ cursor: 'pointer', color: grey[2] }} onClick={() => invalidator.invalidate()} />
            </Row>
            <Row>
              <Input.Search
                placeholder='Search'
                value={search}
                onChange={e => setSearch(e.target.value ?? '')}
                allowClear
              />
            </Row>
          </VSpace>

          {!isDeletedUser && <SidebarCreateButton text='Create New User' onClick={() => onUserSelect('')} />}
        </VSpace>
      </div>

      <ConfigProvider
        theme={{
          algorithm: theme.darkAlgorithm,
        }}
        renderEmpty={() =>
          <Empty
            description='No items found'
            image={<FontAwesomeIcon icon={faInbox} size='xl' />}
          />
        }
      >
        {usersQuery.isFetching ? (
          <Flex vertical align='center' style={{ paddingTop: '2rem' }}>
            <FontAwesomeIcon icon={faSpinnerThird} color={grey[3]} spin />
            <Text type='secondary'>Loading users</Text>
          </Flex>
        ) : (
          <div className='react-scrollable-list' style={{ height: `calc(100vh - ${ !isDeletedUser ? '160px' : '90px' })` }}>
            {!isSearchActive && !isDeletedUser ? <>
                <Text style={{ padding: '10px 10px', fontWeight: 'bold' }}>Active Users</Text>
                <UsersSidebarList listData={activeUsers} onUserSelect={onUserSelect} />
                <Text style={{ padding: '10px 10px', fontWeight: 'bold' }}>Inactive Users</Text>
                <UsersSidebarList listData={inactiveUsers} onUserSelect={onUserSelect} />
              </>
              : <UsersSidebarList listData={filteredUsers} onUserSelect={onUserSelect} />
            }
          </div>
        )}
      </ConfigProvider>
    </PageSidebar>
  )
}

export default UsersSidebar
